import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { FormatBold, FormatColorText, FormatColorFill, TableRows, TableRowsOutlined } from '@mui/icons-material';
import ColorPicker from '../ColorPicker';
import { isBoldCell } from '../../utils/tobTableEditing';

// Formatting toolbar for an editable TOB table -- acts on whatever cell(s) are currently
// selected in the table below it. Shared by the main Auto Quotation TOB table, the
// custom TOB plan builder and the Quotation Preview.
export default function TOBTableMenuBar({ cell, selectedCount, canMerge, canUnmerge, onToggleBold, onSetTextColor, onSetBackgroundColor, onMerge, onUnmerge }) {
    const disabled = selectedCount === 0;

    return (
        <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 0.25,
            mb: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            backgroundColor: '#fff',
            // Compact sizing; also reaches the ColorPicker buttons without changing that shared component.
            '& .MuiIconButton-root': { p: 0.5 },
            '& .MuiSvgIcon-root': { fontSize: 18 },
        }}>
            <Tooltip title="Bold">
                <span>
                    <IconButton size="small" disabled={disabled} onClick={onToggleBold} color={isBoldCell(cell) ? 'primary' : 'default'}>
                        <FormatBold />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Box sx={{ display: 'flex', opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                <ColorPicker
                    title="Text Color"
                    type="text"
                    icon={<FormatColorText />}
                    activeColor={cell?.cell_text_color}
                    onSelect={onSetTextColor}
                    onClear={() => onSetTextColor(undefined)}
                />

                <ColorPicker
                    title="Background Color"
                    type="highlight"
                    icon={<FormatColorFill />}
                    activeColor={cell?.cell_bg_color}
                    onSelect={onSetBackgroundColor}
                    onClear={() => onSetBackgroundColor(undefined)}
                />
            </Box>

            {/* Merging only exists for TOB tables; the Quotation Preview reuses this bar without it. */}
            {(onMerge || onUnmerge) && (
                <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    <Tooltip title="Merge Cells">
                        <span>
                            <IconButton size="small" disabled={!canMerge} onClick={onMerge}>
                                <TableRows />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Unmerge Cells">
                        <span>
                            <IconButton size="small" disabled={!canUnmerge} onClick={onUnmerge}>
                                <TableRowsOutlined />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            )}

            <Typography color="text.secondary" sx={{ fontSize: 11, ml: 1 }}>
                {disabled
                    ? 'Click a cell to select it. Shift-click for a range, Ctrl-click to add one.'
                    : `${selectedCount} cell${selectedCount === 1 ? '' : 's'} selected.`}
            </Typography>
        </Box>
    );
}
